import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useLocalAuth } from "@/components/auth/ClerkAuthProvider";
import { LayoutDashboard, CalendarDays, Users, BadgePercent, Wallet, BarChart3, Menu, Plus, Search, Settings, UserPlus, Truck, Package, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { AccessGate } from "@/components/AccessGate";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { PatientFormDialog } from "@/components/PatientFormDialog";
import { SettingsDialog } from "@/components/SettingsDialog";
import { useSettings } from "@/lib/settings";

const NAV = [
  { to: "/", label: "Painel", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/agenda", label: "Agendamentos", icon: CalendarDays, id: "agenda" },
  { to: "/pacientes", label: "Pacientes", icon: Users, id: "pacientes" },
  { to: "/mensalidades", label: "Mensalidades & Retornos", icon: BadgePercent, id: "mensalidades" },
  { to: "/financeiro", label: "Gestão financeira", icon: Wallet, id: "financeiro" },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, id: "relatorios" },
  { to: "/marketing", label: "Marketing e Redes Sociais", icon: Instagram, id: "marketing" },
  { to: "/fornecedores-compras", label: "Fornecedores e Compras", icon: Truck, id: "fornecedores-compras" },
  { to: "/materiais-estoque", label: "Materiais e Estoque", icon: Package, id: "materiais-estoque" },
];

const SideNav = ({ onNavigate, onSettings }) => {
  const s = useSettings();
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]">
      <div className="px-6 pt-7 pb-8">
        <div className="flex items-center gap-2.5">
          <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl text-white shadow-sm" style={{ backgroundColor: s.primary_color || "#0284c7" }}>
            {s.logo_data ? <img src={s.logo_data} alt={s.clinic_name || "Logotipo da clínica"} className="h-full w-full object-contain bg-white p-0.5" /> : <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M7 2C4.2 2 2.5 4.3 2.5 7c0 2.2.8 3.4 2.4 5.3.4 1.1.9 1.7 2.6 1.7s2.2-.6 2.6-1.7c.8-2.1.8-5.3 2.4-5.3 1.1 0 1.7-1.4 2.1-3.4.5-2.3.5-4.6 1.1-6.6.5-1.6 1.3-2.8 1.3-5 0-2.7-1.7-5-4.5-5-2.1 0-3.2 1.1-5 1.1S9.1 2 7 2z"/></svg>}
          </div>
          <div className="min-w-0">
            <p className="font-display text-[15px] font-extrabold tracking-tight text-slate-900 truncate" data-testid="brand-name">{s.clinic_name || "OdonttoFlow"}</p>
            <p className="text-[11px] text-slate-500 truncate max-w-[150px]">{s.clinic_name ? "OdonttoFlow" : "Consultório odontológico"}</p>
          </div>
        </div>
      </div>
      <nav className="shrink-0 space-y-1 px-3 pb-3">
        {NAV.map(({ to, label, icon: Icon, end, id }) => (
          <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${id}`}
            className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors duration-200 ${isActive ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"}`}>
            <Icon className="h-[18px] w-[18px]" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="m-3 rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-sky-700/70">Profissional</p>
        <p className="mt-1 text-sm font-semibold text-slate-800" data-testid="sidebar-dentist-name">{s.dentist_name || "Configure seu nome"}</p>
        <p className="text-xs text-slate-500">{s.cro || "CRO não informado"}</p>
        <Button variant="ghost" size="sm" onClick={onSettings} className="mt-2 -ml-2 h-8 text-sky-700 hover:bg-sky-100" data-testid="open-settings-btn">
          <Settings className="mr-1.5 h-4 w-4" /> Configurações
        </Button>
      </div>
    </div>
  );
};

const Shell = () => {
  const navigate = useNavigate();
  const {signOut} = useLocalAuth();
  const [menu, setMenu] = useState(false);
  const [newAppt, setNewAppt] = useState(false);
  const [newPatient, setNewPatient] = useState(false);
  const [settings, setSettings] = useState(false);
  const [q, setQ] = useState("");

  const search = (e) => {
    e.preventDefault();
    navigate(`/pacientes?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="flex min-h-screen w-full max-w-full overflow-x-clip bg-slate-50">
      <aside className="no-print fixed inset-y-0 left-0 hidden w-64 border-r border-slate-200/70 bg-white lg:block">
        <SideNav onSettings={() => setSettings(true)} />
      </aside>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="h-[100dvh] max-h-[100dvh] w-72 overflow-hidden p-0">
          <SideNav onNavigate={() => setMenu(false)} onSettings={() => { setMenu(false); setSettings(true); }} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="no-print sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200/70 bg-white/80 px-4 py-3 backdrop-blur-xl sm:px-8">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMenu(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <form onSubmit={search} className="relative hidden max-w-sm flex-1 sm:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar paciente por nome, CPF ou telefone"
              className="h-10 rounded-full border-slate-200 bg-slate-50 pl-9 focus-visible:bg-white" data-testid="global-search-input" />
          </form>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" className="hidden rounded-full border-sky-200 text-sky-700 hover:bg-sky-50 md:inline-flex" onClick={() => setNewPatient(true)} data-testid="header-new-patient-btn">
              <UserPlus className="mr-1.5 h-4 w-4" /> Paciente
            </Button>
            <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setNewAppt(true)} data-testid="header-new-appointment-btn">
              <Plus className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Novo agendamento</span><span className="sm:hidden">Agendar</span>
            </Button>
            <div className="ml-1" data-testid="user-menu"><Button variant="outline" onClick={() => {signOut();navigate("/sign-in");}}>Sair</Button></div>
          </div>
        </header>
        <main className="print-full premium-workspace min-w-0 w-full max-w-full flex-1 px-4 py-6 sm:px-8 sm:py-8">
          <Outlet />
        </main>
      </div>
      <AppointmentDialog open={newAppt} onOpenChange={setNewAppt} />
      <PatientFormDialog open={newPatient} onOpenChange={setNewPatient} onSaved={(p) => navigate(`/pacientes/${p.id}`)} />
      <SettingsDialog open={settings} onOpenChange={setSettings} />
    </div>
  );
};

export default function Layout() {
  return (
    <AccessGate>
      <Shell />
    </AccessGate>
  );
}
